import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useGameStore } from '../store';
import {
  playClick,
  playToggle,
  playPopup,
  onMusicToggle,
  ensureAudioContext,
} from '../sounds';
import { trackEvent } from '../analytics';
import { Button, Popup, Heading, Muted } from './ui';
import { AudioSettings, SettingsGearIcon, BugIcon } from './SettingsPanel';
import { Preferences } from './Preferences';
import './TopBar.css';

interface TopBarProps {
  navMode: 'disabled' | 'back' | 'exit';
  onBack: () => void;
  confirmExit?: boolean;
  onRestart?: () => void;
  onShowHelp: () => void;
  onTogglePrivacy?: () => void;
  /** True when actively playing a level — locks level rules. */
  isPlaying?: boolean;
}

export function TopBar({
  navMode,
  onBack,
  confirmExit,
  onRestart,
  onShowHelp,
  onTogglePrivacy,
  isPlaying = false,
}: TopBarProps) {
  const musicOn = useGameStore((s) => s.musicEnabled);
  const toggleMusic = useGameStore((s) => s.toggleMusic);
  const autoPauseEnabled = useGameStore((s) => s.autoPauseEnabled);
  const toggleAutoPause = useGameStore((s) => s.toggleAutoPause);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const bugBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const feedback = Sentry.getFeedback();
    const btn = bugBtnRef.current;
    if (!feedback || !btn) return;
    const unsubscribe = feedback.attachTo(btn);
    return () => {
      unsubscribe?.();
    };
  }, []);

  const handleNav = () => {
    playClick();
    if (confirmExit) {
      playPopup();
      setShowConfirm(true);
    } else {
      onBack();
    }
  };

  const handleConfirm = () => {
    playClick();
    setShowConfirm(false);
    onBack();
  };

  return (
    <>
      <div className="top-bar">
        <div className="top-bar-left">
          {navMode === 'back' && (
            <Button
              variant="secondary"
              size="circle"
              className="top-bar-btn"
              onClick={handleNav}
              aria-label="Go back"
            >
              {'\u2190'}
            </Button>
          )}
          {navMode === 'exit' && (
            <Button
              variant="secondary"
              size="circle"
              className="top-bar-btn"
              onClick={handleNav}
              aria-label="Exit"
            >
              {'\u2715'}
            </Button>
          )}
        </div>

        <div className="top-bar-right">
          <button
            ref={bugBtnRef}
            type="button"
            className="pushable push-circle btn-secondary top-bar-btn"
            onClick={playClick}
            aria-label="Report a bug"
            title="Report a bug"
          >
            <span className="shadow" />
            <span className="edge" />
            <span className="front"><BugIcon /></span>
          </button>
          <Button
            variant="secondary"
            size="circle"
            className="top-bar-btn"
            onClick={() => {
              playClick();
              onShowHelp();
            }}
            aria-label="Guide"
          >
            ?
          </Button>
          <Button
            variant="secondary"
            size="circle"
            className="top-bar-btn"
            onClick={() => {
              ensureAudioContext();
              playToggle();
              const next = toggleMusic();
              onMusicToggle(next);
              trackEvent('music_toggle', { enabled: next });
            }}
            aria-label={musicOn ? 'Mute music' : 'Enable music'}
          >
            {'\u266A'}
            {!musicOn && <span className="top-bar-off-line" />}
          </Button>
          <Button
            variant="secondary"
            size="circle"
            className="top-bar-btn"
            onClick={() => {
              playClick();
              setShowSettings(true);
            }}
            aria-label="Settings"
          >
            <SettingsGearIcon />
          </Button>
        </div>
      </div>

      {showConfirm && (
        <Popup aria-label="Leave this puzzle?" onDismiss={() => setShowConfirm(false)}>
          <div className="top-bar-confirm">
            <div className="top-bar-confirm__topbar">
              <Heading className="top-bar-confirm__title">Leave this puzzle?</Heading>
              <Button
                variant="secondary"
                size="circle"
                onClick={() => {
                  playClick();
                  setShowConfirm(false);
                }}
                aria-label="Close"
              >
                {'\u2715'}
              </Button>
            </div>
            <Muted className="top-bar-confirm-sub">
              Your progress will be saved.
            </Muted>
            <div className="top-bar-confirm-btns">
              <Button
                variant="primary"
                size="wide-sm"
                onClick={handleConfirm}
              >
                Exit
              </Button>
              {onRestart && (
                <Button
                  variant="ghost"
                  size="wide-sm"
                  onClick={() => {
                    playClick();
                    setShowConfirm(false);
                    onRestart();
                  }}
                >
                  Restart
                </Button>
              )}
            </div>
          </div>
        </Popup>
      )}

      {showSettings && (
        <Popup aria-label="Settings" onDismiss={() => setShowSettings(false)} maxWidth={640}>
          <div className="top-bar-settings">
            <div className="top-bar-settings-header">
              <Heading>Settings</Heading>
              <Button
                variant="secondary"
                size="circle"
                className="top-bar-btn"
                onClick={() => {
                  playClick();
                  setShowSettings(false);
                }}
                aria-label="Close settings"
              >
                {'\u2715'}
              </Button>
            </div>

            <div className="top-bar-settings-body">
            <div className="top-bar-settings-group">
              <div className="top-bar-settings-section">
                <div className="top-bar-settings-section-title">General</div>
                <AudioSettings />
                <button
                  className="top-bar-setting-row"
                  onClick={() => {
                    playToggle();
                    const next = toggleAutoPause();
                    trackEvent('auto_pause_toggle', { enabled: next });
                  }}
                >
                  <div className="top-bar-setting-info">
                    <span className="top-bar-setting-label">Auto-Pause</span>
                    <span className="top-bar-setting-desc">
                      Pause the game when you switch tabs
                    </span>
                  </div>
                  <div
                    className={`top-bar-setting-toggle${autoPauseEnabled ? ' top-bar-setting-toggle--on' : ''}`}
                    aria-hidden="true"
                  >
                    <div className="top-bar-setting-knob" />
                  </div>
                </button>
              </div>

              <Preferences />
            </div>
            <div className="top-bar-settings-footer">
              {onTogglePrivacy && (
                <button
                  className="top-bar-settings-footer-btn"
                  onClick={() => {
                    playClick();
                    setShowSettings(false);
                    onTogglePrivacy();
                  }}
                >
                  Privacy settings
                </button>
              )}
              <span className="top-bar-settings-footer-version">
                v{__BUILD_DATE__} ({__COMMIT_SHA__})
              </span>
            </div>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
}
